import type { ComponentProps, ReactNode, RefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { createGlobalStyle } from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import clsx from 'clsx'

import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import InputText from 'LuxKit/Typography/InputText'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { DROPDOWN_SHEET_MAX_HEIGHT_CSS_VARIABLE } from 'LuxKit/Dropdown/_DropdownSheet'
import LineEditIcon from 'LuxKit/Icons/line/LineEditIcon'
import LineAngleLeftBIcon from 'LuxKit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import IconButton from 'LuxKit/Button/IconButton'
import Counter, { type CounterChangeEventHandler } from 'LuxKit/Counter'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import DateRangePickerDay from 'components/Common/Calendar/DateRangePickerDay'
import { INTL_LONG_MONTH_LONG_YEAR, ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { TripStop } from 'tripPlanner/api/types'
import { formatStopDateRange } from 'tripPlanner/utils/chatDates'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { sortTripStopsByItineraryOrder } from 'tripPlanner/utils/itineraryStopOrder'
import { STOP_TIMING_EDITOR_DROPDOWN_Z_INDEX } from './stopTimingLayers'
import { useAssistantSurfaceDropdownBoundary } from 'tripPlanner/assistant/layout/AssistantSurfaceLayoutContext'
import useDidChange from 'hooks/useDidChange'
import useResizeObserver from 'hooks/useResizeObserver'
import {
  getExistingStopDurationDayCount,
  getStopDurationDayCount,
  isIsoDate,
} from './stopTimingUtils'
import {
  addMonths,
  useIntlDateFormatter,
} from 'lib/datetime/dateUtils'

type StopTimingEditVariant = 'default' | 'mobile'

interface Props {
  stop: TripStop
  tripStops?: ReadonlyArray<TripStop>
  children: ReactNode
  onSendMessage: (message: string) => Promise<void> | void
  disabled?: boolean
  className?: string
  dropdownPlacement?: ComponentProps<typeof DropdownList>['placement']
  dropdownAnchorRef?: RefObject<HTMLElement | null>
  expandTapTarget?: boolean
  variant?: StopTimingEditVariant
}

type StopTimingEditIntent =
  | { type: 'set-dates'; startDate: string; endDate: string }
  | { type: 'clear-dates' }
  | { type: 'set-flexible-days'; durationDayCount: number }

type StopTimingEditMode = 'specific' | 'flexible'

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
  padding: 0;
  border: 0;
  border-bottom: 1px dashed transparent;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-bottom-color: currentColor;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
    border-bottom-color: transparent;
  }

  &.is-mobile {
    min-height: ${rem(40)};
    padding-block: ${rem(4)};
  }
`

const TriggerContent = styled.span`
  min-width: 0;
`

const EditIconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  opacity: 0.72;
`

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  width: ${rem(440)};
  max-width: calc(100vw - ${rem(48)});
  padding: ${rem(4)} ${rem(20)} ${rem(20)};
`

const ModeToggle = styled(SegmentedOptionsGroup<StopTimingEditMode>)`
  align-self: center;
  width: min(${rem(320)}, 100%);
`

const DurationField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(12)};
`

const DurationCounter = styled(Counter)`
  width: ${rem(152)};
  justify-content: space-between;

  > .variant-text-regular {
    min-width: ${rem(64)};
    white-space: nowrap;
  }
`

const FieldLabel = styled(InputText)`
  display: block;
`

const CalendarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`

const CalendarHeader = styled.div`
  display: grid;
  grid-template: "prev-button heading next-button" auto / ${rem(32)} 1fr ${rem(32)};
  align-items: center;
`

const CalendarHeading = styled(Heading)`
  grid-area: heading;
`

const PreviousMonthButton = styled(IconButton)`
  grid-area: prev-button;
`

const NextMonthButton = styled(IconButton)`
  grid-area: next-button;
`

const WeekdayHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  align-items: center;
  row-gap: ${rem(12)};
`

const WeekdayLabel = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-auto-rows: ${rem(52)};
  align-items: center;
`

const DateCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const StopDateRangePickerDay = styled(DateRangePickerDay)`
  width: 100%;
  min-height: ${rem(52)};
`

const StopTimingEditorDropdownLayer = createGlobalStyle`
  [data-stop-timing-editor-dropdown] {
    z-index: ${STOP_TIMING_EDITOR_DROPDOWN_Z_INDEX};
  }

  [data-stop-timing-editor-dropdown] > div > div:first-child {
    max-height: none;
  }
`

const STOP_TIMING_EDITOR_VIEWPORT_GAP_PX = 8
const STOP_TIMING_EDITOR_MIN_SHEET_HEIGHT_PX = 180

function getDraftStopDurationDayCount(stop: TripStop): number {
  return getExistingStopDurationDayCount(stop) ?? 1
}

function getStopReferenceDate(stop: TripStop): string | undefined {
  if (stop.endDate && isIsoDate(stop.endDate)) return stop.endDate
  if (stop.days?.length) {
    for (let index = stop.days.length - 1; index >= 0; index -= 1) {
      const dayDate = stop.days[index].date
      if (dayDate && isIsoDate(dayDate)) return dayDate
    }
  }
  if (stop.startDate && isIsoDate(stop.startDate)) return stop.startDate
}

function getPreviousStopDatePickerDefaultDate(stop: TripStop, tripStops: ReadonlyArray<TripStop> | undefined): string | undefined {
  if (stop.startDate || stop.endDate || !tripStops?.length) return undefined

  const orderedStops = sortTripStopsByItineraryOrder([...tripStops])
  const stopIndex = orderedStops.findIndex(candidate => candidate.id === stop.id)
  if (stopIndex <= 0) return undefined

  for (let index = stopIndex - 1; index >= 0; index -= 1) {
    const referenceDate = getStopReferenceDate(orderedStops[index])
    if (referenceDate) return referenceDate
  }
}

function getMomentDate(date: string | undefined) {
  if (!date) return undefined

  const parsedDate = moment(date, ISO_DATE_FORMAT, true)
  if (!parsedDate.isValid()) return undefined
  return parsedDate
}

function formatEnglishDurationLabel(dayCount: number): string {
  return `${dayCount} ${dayCount === 1 ? 'day' : 'days'}`
}

function formatDateRangeOrFallback(startDate: string | undefined, endDate: string | undefined): string {
  if (startDate && endDate && isIsoDate(startDate) && isIsoDate(endDate)) {
    return formatStopDateRange(startDate, endDate)
  }

  return 'not set'
}

function getInitialEditMode(stop: TripStop): StopTimingEditMode {
  return stop.startDate || stop.endDate ? 'specific' : 'flexible'
}

function resetDropdownViewportConstraints(dropdownBody: HTMLDivElement | null) {
  const dropdown = dropdownBody?.closest<HTMLElement>('[data-stop-timing-editor-dropdown]')

  if (dropdown) {
    dropdown.style.translate = ''
    dropdown.style.removeProperty(DROPDOWN_SHEET_MAX_HEIGHT_CSS_VARIABLE)
  }

  if (dropdownBody) {
    dropdownBody.style.maxHeight = ''
  }
}

function constrainDropdownToViewport(dropdownBody: HTMLDivElement | null, boundary: HTMLElement | null | undefined) {
  if (typeof window === 'undefined' || !dropdownBody) return

  const dropdown = dropdownBody.closest<HTMLElement>('[data-stop-timing-editor-dropdown]')
  const sheet = dropdown?.firstElementChild as HTMLElement | null | undefined
  const footer = sheet?.lastElementChild as HTMLElement | null | undefined
  if (!dropdown || !sheet) return

  resetDropdownViewportConstraints(dropdownBody)

  const dropdownRect = dropdown.getBoundingClientRect()
  const naturalSheetHeight = sheet.getBoundingClientRect().height
  const boundaryRect = boundary?.getBoundingClientRect()
  const visibleTop = Math.max(STOP_TIMING_EDITOR_VIEWPORT_GAP_PX, boundaryRect?.top ?? STOP_TIMING_EDITOR_VIEWPORT_GAP_PX)
  const visibleBottom = Math.min(window.innerHeight - STOP_TIMING_EDITOR_VIEWPORT_GAP_PX, boundaryRect?.bottom ?? window.innerHeight - STOP_TIMING_EDITOR_VIEWPORT_GAP_PX)
  const maxDropdownTop = visibleBottom - naturalSheetHeight
  let shiftY = 0
  if (dropdownRect.top > maxDropdownTop) {
    shiftY = Math.max(visibleTop - dropdownRect.top, maxDropdownTop - dropdownRect.top)
  }
  const constrainedTop = Math.max(visibleTop, dropdownRect.top + shiftY)
  const availableSheetHeight = Math.max(
    STOP_TIMING_EDITOR_MIN_SHEET_HEIGHT_PX,
    visibleBottom - constrainedTop,
  )

  dropdown.style.translate = shiftY ? `0 ${shiftY}px` : ''
  dropdown.style.setProperty(DROPDOWN_SHEET_MAX_HEIGHT_CSS_VARIABLE, `${availableSheetHeight}px`)

  if (naturalSheetHeight > availableSheetHeight) {
    const footerHeight = footer?.getBoundingClientRect().height ?? 0
    const availableBodyHeight = Math.max(0, availableSheetHeight - footerHeight)

    dropdownBody.style.maxHeight = `${availableBodyHeight}px`
  }
}

function buildStopTimingEditMessage(stop: TripStop, intent: StopTimingEditIntent, visibleMessage: string): string {
  if (intent.type === 'set-flexible-days') {
    return appendContextTag(
      visibleMessage,
      [
        'The customer used the stop date picker in flexible dates mode.',
        `Target stop_id=${stop.id} "${stop.placeName}".`,
        `Set this stop to ${formatEnglishDurationLabel(intent.durationDayCount)}.`,
        'Remove any existing startDate and endDate from the stop. Do not set, infer, or pass through specific dates for this update.',
        'Adjust neighbouring stops and itinerary dates as needed so the full trip remains coherent.',
      ].join(' '),
      TRIP_PLANNER_ACTION_CONTEXT_LABEL,
    )
  }

  const previousRange = formatDateRangeOrFallback(stop.startDate, stop.endDate)
  const previousStartDate = stop.startDate ?? 'not set'
  const previousEndDate = stop.endDate ?? 'not set'
  const previousDuration = getExistingStopDurationDayCount(stop)
  const contextPrefix = [
    'The customer used the stop days and dates editor in the itinerary UI.',
    `Target stop_id=${stop.id} "${stop.placeName}".`,
    `Current values: date range ${previousRange} (startDate=${previousStartDate}, endDate=${previousEndDate}, duration=${previousDuration ? formatEnglishDurationLabel(previousDuration) : 'not set'}).`,
  ].join(' ')
  const nextInstruction = (() => {
    if (intent.type === 'set-dates') {
      return `Set the stop dates to ${formatStopDateRange(intent.startDate, intent.endDate)} (startDate=${intent.startDate}, endDate=${intent.endDate}).`
    }

    return 'Remove the stop startDate and endDate. Keep the stop flexible duration unchanged unless the model must rebalance neighbouring stops.'
  })()

  return appendContextTag(
    visibleMessage,
    [
      contextPrefix,
      nextInstruction,
      'Adjust neighbouring stops and itinerary dates as needed so the full trip remains coherent.',
    ].join(' '),
    TRIP_PLANNER_ACTION_CONTEXT_LABEL,
  )
}

interface InlineStopDateRangePickerProps {
  startDate?: moment.Moment
  endDate?: moment.Moment
  initDate?: moment.Moment
  onDatesChange: (dates: { startDate: moment.Moment, endDate?: moment.Moment }) => void
  startLabel: string
  endLabel: string
}

function InlineStopDateRangePicker({
  startDate,
  endDate,
  initDate,
  onDatesChange,
  startLabel,
  endLabel,
}: InlineStopDateRangePickerProps) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const [visibleDate, setVisibleDate] = useState(() => initDate?.toDate() || startDate?.toDate() || new Date())
  const startDateChanged = useDidChange(startDate?.valueOf())

  useEffect(() => {
    if (!startDate || !startDateChanged) return

    const startIsVisible = startDate.isSame(visibleDate, 'month')
    if (!startIsVisible) {
      setVisibleDate(startDate.toDate())
    }
  }, [startDate, startDateChanged, visibleDate])

  const formattedVisibleDate = useMemo(() => {
    const momentDate = moment(visibleDate)
    if (!momentDate.isValid()) return ''
    return dateFormatter(momentDate, INTL_LONG_MONTH_LONG_YEAR)
  }, [dateFormatter, visibleDate])

  const handlePreviousMonth = useCallback(() => {
    setVisibleDate(currentDate => addMonths(currentDate, -1))
  }, [])

  const handleNextMonth = useCallback(() => {
    setVisibleDate(currentDate => addMonths(currentDate, 1))
  }, [])

  const handleDayClick = useCallback((day: moment.Moment) => {
    const isSameStartDay = startDate && day.isSame(startDate, 'day')
    const shouldSelectEndDate = startDate && !endDate && (day.isAfter(startDate) || isSameStartDay)

    if (shouldSelectEndDate) {
      onDatesChange({ startDate, endDate: day })
      return
    }

    onDatesChange({ startDate: day, endDate: undefined })
  }, [endDate, onDatesChange, startDate])

  const weekdayLabels = useMemo(() => [
    intl.formatMessage({
      id: 'K_fjVt-jvqi6oWtBjxmM',
      defaultMessage: 'Mon',
      description: 'Abbreviated Monday label in the stop date picker weekday header',
    }),
    intl.formatMessage({
      id: 'cmNaH3ww08d8ZVPb0z-M',
      defaultMessage: 'Tue',
      description: 'Abbreviated Tuesday label in the stop date picker weekday header',
    }),
    intl.formatMessage({
      id: '9ef76Thp0Hy2Ieb7jSDs',
      defaultMessage: 'Wed',
      description: 'Abbreviated Wednesday label in the stop date picker weekday header',
    }),
    intl.formatMessage({
      id: 'iK56e2q_BjZHr_uBYdaT',
      defaultMessage: 'Thu',
      description: 'Abbreviated Thursday label in the stop date picker weekday header',
    }),
    intl.formatMessage({
      id: 'xK5JuTFcTzERJnjl5hBd',
      defaultMessage: 'Fri',
      description: 'Abbreviated Friday label in the stop date picker weekday header',
    }),
    intl.formatMessage({
      id: 'S3CFjGI0wmoFULbZFU-E',
      defaultMessage: 'Sat',
      description: 'Abbreviated Saturday label in the stop date picker weekday header',
    }),
    intl.formatMessage({
      id: 'G1JtlyomQGaAm5dK0Trh',
      defaultMessage: 'Sun',
      description: 'Abbreviated Sunday label in the stop date picker weekday header',
    }),
  ], [intl])

  const calendarDates = useMemo(() => {
    const monthStart = moment(visibleDate).startOf('month')
    const leadingBlankCount = monthStart.isoWeekday() - 1
    const daysInMonth = monthStart.daysInMonth()
    const dates: Array<moment.Moment | undefined> = Array.from({ length: leadingBlankCount }, () => undefined)

    for (let day = 1; day <= daysInMonth; day += 1) {
      dates.push(monthStart.clone().date(day))
    }

    return dates
  }, [visibleDate])

  return (
    <CalendarContent>
      <CalendarHeader>
        <PreviousMonthButton
          kind="tertiary"
          size="small"
          shape="circle"
          horizontalOutdent="start"
          onClick={handlePreviousMonth}
          aria-label={intl.formatMessage({
            id: 'HMo-K8sh2Zy_wQcGa7Ui',
            defaultMessage: 'Previous month',
            description: 'Accessible label for moving the stop date picker to the previous month',
          })}
        >
          <LineAngleLeftBIcon />
        </PreviousMonthButton>
        <CalendarHeading variant="heading6" align="center">
          {formattedVisibleDate}
        </CalendarHeading>
        <NextMonthButton
          kind="tertiary"
          size="small"
          shape="circle"
          horizontalOutdent="end"
          onClick={handleNextMonth}
          aria-label={intl.formatMessage({
            id: 'mSV-P5L2NnDB1PN-Okuj',
            defaultMessage: 'Next month',
            description: 'Accessible label for moving the stop date picker to the next month',
          })}
        >
          <LineAngleRightBIcon />
        </NextMonthButton>
      </CalendarHeader>
      <WeekdayHeader>
        {weekdayLabels.map(label => (
          <WeekdayLabel key={label} variant="medium" weight="bold" align="center">
            {label}
          </WeekdayLabel>
        ))}
      </WeekdayHeader>
      <DateGrid>
        {calendarDates.map((date, index) => {
          if (!date) return <DateCell key={`empty-${index}`} />

          const isStartDate = !!startDate && date.isSame(startDate, 'day')
          const isEndDate = !!endDate && date.isSame(endDate, 'day')
          const isSelected = isStartDate || isEndDate
          const isInRange = !!(startDate && endDate && date.isAfter(startDate, 'day') && date.isBefore(endDate, 'day'))

          return (
            <DateCell key={date.format(ISO_DATE_FORMAT)}>
              <StopDateRangePickerDay
                date={date.toDate()}
                onClick={() => handleDayClick(date)}
                selected={isSelected}
                inRange={isInRange}
                label={isStartDate ? startLabel : isEndDate ? endLabel : undefined}
                reserveLabelSpacing={false}
              />
            </DateCell>
          )
        })}
      </DateGrid>
    </CalendarContent>
  )
}

function StopTimingEditControl({
  stop,
  tripStops,
  children,
  onSendMessage,
  disabled = false,
  className,
  dropdownPlacement,
  dropdownAnchorRef,
  expandTapTarget = false,
  variant = 'default',
}: Props) {
  const intl = useIntl()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownBodyRef = useRef<HTMLDivElement | null>(null)
  const dropdownBoundary = useAssistantSurfaceDropdownBoundary()
  const isMobileVariant = variant === 'mobile'
  const [open, setOpen] = useState(false)
  const [editMode, setEditMode] = useState<StopTimingEditMode>(() => getInitialEditMode(stop))
  const [startDate, setStartDate] = useState(stop.startDate ?? '')
  const [endDate, setEndDate] = useState(stop.endDate ?? '')
  const [durationDayCount, setDurationDayCount] = useState(() => getDraftStopDurationDayCount(stop))
  const [isSending, setIsSending] = useState(false)
  const initialDurationDayCount = getDraftStopDurationDayCount(stop)
  const initialEditMode = getInitialEditMode(stop)
  const resolvedDropdownPlacement = dropdownPlacement ?? (isMobileVariant ? 'bottom' : 'bottom-start')

  const resetDraft = useCallback(() => {
    setEditMode(getInitialEditMode(stop))
    setStartDate(stop.startDate ?? '')
    setEndDate(stop.endDate ?? '')
    setDurationDayCount(getDraftStopDurationDayCount(stop))
  }, [stop])

  useEffect(() => {
    if (open && !isMobileVariant) return
    resetDraft()
  }, [isMobileVariant, open, resetDraft])

  const constrainDropdown = useCallback(() => {
    constrainDropdownToViewport(dropdownBodyRef.current, dropdownBoundary.boundaryRef?.current)
  }, [dropdownBoundary.boundaryRef])

  const queueDropdownConstraint = useCallback(() => {
    window.requestAnimationFrame(constrainDropdown)
  }, [constrainDropdown])

  useEffect(() => {
    if (!open) {
      resetDropdownViewportConstraints(dropdownBodyRef.current)
      return
    }

    const dropdownBody = dropdownBodyRef.current
    const animationFrame = window.requestAnimationFrame(constrainDropdown)

    window.addEventListener('resize', constrainDropdown)
    window.addEventListener('scroll', constrainDropdown, true)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      window.removeEventListener('resize', constrainDropdown)
      window.removeEventListener('scroll', constrainDropdown, true)
      resetDropdownViewportConstraints(dropdownBody)
    }
  }, [constrainDropdown, open])

  useResizeObserver(dropdownBodyRef, () => {
    if (!open) return

    queueDropdownConstraint()
  })

  const openDropdown = useCallback(() => {
    if (disabled) return
    setOpen(true)
  }, [disabled])

  const closeDropdown = useCallback(() => {
    setOpen(false)
  }, [])

  const previousStopDatePickerDefaultDate = useMemo(
    () => getPreviousStopDatePickerDefaultDate(stop, tripStops),
    [stop, tripStops],
  )

  const startDateMoment = useMemo(() => getMomentDate(startDate), [startDate])
  const endDateMoment = useMemo(() => getMomentDate(endDate), [endDate])
  const datePickerInitDate = useMemo(
    () => getMomentDate(stop.startDate ?? previousStopDatePickerDefaultDate),
    [previousStopDatePickerDefaultDate, stop.startDate],
  )

  const handleDatesChange = useCallback((dates: { startDate: moment.Moment, endDate?: moment.Moment }) => {
    setStartDate(dates.startDate.format(ISO_DATE_FORMAT))
    setEndDate(dates.endDate?.format(ISO_DATE_FORMAT) ?? '')
    queueDropdownConstraint()
  }, [queueDropdownConstraint])

  const handleDurationDayCountChange = useCallback<CounterChangeEventHandler>((value) => {
    setDurationDayCount(value)
  }, [])

  const handleModeChange = useCallback((value: StopTimingEditMode) => {
    setEditMode(value)
    queueDropdownConstraint()
  }, [queueDropdownConstraint])

  const clearDraftValue = useCallback(() => {
    if (editMode === 'flexible') {
      setDurationDayCount(1)
      queueDropdownConstraint()
      return
    }

    setStartDate('')
    setEndDate('')
    queueDropdownConstraint()
  }, [editMode, queueDropdownConstraint])

  const hasStartDate = Boolean(startDate)
  const hasEndDate = Boolean(endDate)
  const hasNoDates = !hasStartDate && !hasEndDate
  const hasCompleteDateRange = hasStartDate && hasEndDate
  const dateRangeDayCount = hasCompleteDateRange ? getStopDurationDayCount(startDate, endDate) : undefined
  const existingDurationDayCount = getExistingStopDurationDayCount(stop)
  const hasExistingDates = Boolean(stop.startDate || stop.endDate)
  const datesChanged = startDate !== (stop.startDate ?? '') || endDate !== (stop.endDate ?? '')
  const draftDurationChanged = durationDayCount !== initialDurationDayCount
  const durationChanged = durationDayCount !== existingDurationDayCount
  const modeChanged = editMode !== initialEditMode
  const specificDateRangeIsValid = hasNoDates || Boolean(dateRangeDayCount)
  const hasUserChange = datesChanged || draftDurationChanged || modeChanged
  const canSaveSpecificDates = editMode === 'specific' && specificDateRangeIsValid && datesChanged
  const canSaveFlexibleDays = editMode === 'flexible' && (hasExistingDates || durationChanged || !existingDurationDayCount)
  const canSave = (canSaveSpecificDates || canSaveFlexibleDays) && (!isMobileVariant || hasUserChange)

  const intent = useMemo<StopTimingEditIntent | undefined>(() => {
    if (!canSave) return undefined

    if (editMode === 'flexible') {
      return { type: 'set-flexible-days', durationDayCount }
    }

    if (hasCompleteDateRange && dateRangeDayCount) {
      return { type: 'set-dates', startDate, endDate }
    }

    if (datesChanged && hasNoDates) {
      return { type: 'clear-dates' }
    }

    return undefined
  }, [canSave, dateRangeDayCount, datesChanged, durationDayCount, editMode, endDate, hasCompleteDateRange, hasNoDates, startDate])
  const saveDisabled = !intent || (isMobileVariant && (disabled || isSending))

  const visibleMessage = useMemo(() => {
    if (!intent) return ''

    if (intent.type === 'set-dates') {
      return intl.formatMessage(
        {
          id: 'GmPadHCwUTP6pT8V_Mce',
          defaultMessage: 'Update {placeName} dates to {dateRange}',
          description: 'Chat message sent when the user edits a stop date range in AI TripPlanner',
        },
        { placeName: stop.placeName, dateRange: formatStopDateRange(intent.startDate, intent.endDate) },
      )
    }

    if (intent.type === 'clear-dates') {
      return intl.formatMessage(
        {
          id: 'luacJSmQ6f8RoPcJDTQ4',
          defaultMessage: 'Remove dates for {placeName}',
          description: 'Chat message sent when the user clears stop dates in AI TripPlanner',
        },
        { placeName: stop.placeName },
      )
    }

    return intl.formatMessage(
      {
        id: 'y16c_w9cJ6IfIaCJhI02',
        defaultMessage: 'Update {placeName} to {duration}',
        description: 'Chat message sent when the user edits a stop flexible duration in AI TripPlanner',
      },
      {
        placeName: stop.placeName,
        duration: intl.formatMessage(
          {
            id: 'uHGpY77lIYQwkJVBl1uE',
            defaultMessage: '{dayCount, plural, one {# day} other {# days}}',
            description: 'Flexible day count label in the stop date picker for AI TripPlanner',
          },
          { dayCount: intent.durationDayCount },
        ),
      },
    )
  }, [intent, intl, stop.placeName])

  const handleSave = useCallback(async() => {
    if (saveDisabled || !intent) return
    const message = buildStopTimingEditMessage(stop, intent, visibleMessage)
    if (!isMobileVariant) {
      onSendMessage(message)
      closeDropdown()
      return
    }

    setIsSending(true)
    try {
      await onSendMessage(message)
      closeDropdown()
    } catch {
      setIsSending(false)
      return
    }
    setIsSending(false)
  }, [closeDropdown, intent, isMobileVariant, onSendMessage, saveDisabled, stop, visibleMessage])

  const formatDurationValue = useCallback((dayCount: number) => intl.formatMessage(
    {
      id: 'uHGpY77lIYQwkJVBl1uE',
      defaultMessage: '{dayCount, plural, one {# day} other {# days}}',
      description: 'Flexible day count label in the stop date picker for AI TripPlanner',
    },
    { dayCount },
  ), [intl])

  const editLabel = intl.formatMessage(
    {
      id: 'hZ1ob9L2hg1ZiJyodP5i',
      defaultMessage: 'Edit days and dates for {placeName}',
      description: 'Accessible label for editing stop days and dates in AI TripPlanner',
    },
    { placeName: stop.placeName },
  )

  const durationLabel = intl.formatMessage({
    id: 'Jp0bXgptNeO35mQjnVsu',
    defaultMessage: 'How many days?',
    description: 'Flexible duration label for editing stop days in AI TripPlanner',
  })

  return (
    <>
      <StopTimingEditorDropdownLayer />
      <TriggerButton
        type="button"
        ref={triggerRef}
        onClick={openDropdown}
        disabled={disabled}
        aria-label={editLabel}
        className={clsx(className, expandTapTarget && 'is-mobile')}
      >
        <TriggerContent>{children}</TriggerContent>
        <EditIconWrap aria-hidden="true">
          <LineEditIcon size="XS" />
        </EditIconWrap>
      </TriggerButton>
      <DropdownList
        size="M"
        open={open}
        onClose={closeDropdown}
        triggerRef={triggerRef}
        anchorRef={dropdownAnchorRef ?? triggerRef}
        placement={resolvedDropdownPlacement}
        boundaryRef={dropdownBoundary.boundaryRef}
        portaled={dropdownBoundary.portaled ?? true}
        crossAxisShift={isMobileVariant ? true : undefined}
        autoHeight={dropdownBoundary.autoHeight}
        bodyRef={dropdownBodyRef}
        data-stop-timing-editor-dropdown
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'W-B8RA3oaApbUDIEz9QY',
            defaultMessage: 'Save',
            description: 'Primary action label for saving stop days and dates in AI TripPlanner',
          }),
          onClick: handleSave,
          disabled: saveDisabled,
        }}
        secondaryActionProps={{
          children: intl.formatMessage({
            id: 'f8ziQ8fnWuTr1qB3shqm',
            defaultMessage: 'Clear',
            description: 'Secondary action label for clearing the current stop date picker mode in AI TripPlanner',
          }),
          onClick: clearDraftValue,
          kind: 'tertiary',
        }}
      >
        <FormContent>
          <ModeToggle
            inputsName={`stop-timing-mode-${stop.id}`}
            distribution="filled"
            onValueChange={handleModeChange}
          >
            <SegmentedOption
              value="specific"
              checked={editMode === 'specific'}
              title={intl.formatMessage({
                id: '3ordzYQAwrQ_2zMtkiRo',
                defaultMessage: 'Specific dates',
                description: 'Specific dates mode label in the stop date picker for AI TripPlanner',
              })}
            />
            <SegmentedOption
              value="flexible"
              checked={editMode === 'flexible'}
              title={intl.formatMessage({
                id: 'TR_KXl5XjCdCay_DX1eq',
                defaultMessage: 'Flexible dates',
                description: 'Flexible dates mode label in the stop date picker for AI TripPlanner',
              })}
            />
          </ModeToggle>
          {editMode === 'specific' ? (
            <InlineStopDateRangePicker
              startDate={startDateMoment}
              endDate={endDateMoment}
              initDate={datePickerInitDate}
              onDatesChange={handleDatesChange}
              startLabel={intl.formatMessage({
                id: 'X3uLmaA_A_xoEGU0dmpc',
                defaultMessage: 'Start',
                description: 'Start date input label for editing a stop date range in AI TripPlanner',
              })}
              endLabel={intl.formatMessage({
                id: 'KXNc1ezaqS83q1l0Gk3b',
                defaultMessage: 'End',
                description: 'End date input label for editing a stop date range in AI TripPlanner',
              })}
            />
          ) : (
            <DurationField>
              <FieldLabel variant="label">
                {durationLabel}
              </FieldLabel>
              <DurationCounter
                value={durationDayCount}
                min={1}
                max={365}
                onChange={handleDurationDayCountChange}
                size="large"
                valueFormatter={formatDurationValue}
                data-testid="stop-duration-counter"
              />
            </DurationField>
          )}
          {!specificDateRangeIsValid && hasCompleteDateRange && (
            <Caption variant="medium" colour="critical">
              {intl.formatMessage({
                id: 'daj9thHEOBrKomMaOOk7',
                defaultMessage: 'Choose an end date on or after the start date.',
                description: 'Inline validation message for editing a stop date range in AI TripPlanner',
              })}
            </Caption>
          )}
          {editMode === 'specific' && (hasStartDate || hasEndDate) && !hasCompleteDateRange && (
            <Caption variant="medium" colour="critical">
              {intl.formatMessage({
                id: 'f-UUnIBQF8-7__fPrfn_',
                defaultMessage: 'Choose both a start and end date, or leave both blank.',
                description: 'Inline validation message for partially filled stop dates in AI TripPlanner',
              })}
            </Caption>
          )}
        </FormContent>
      </DropdownList>
    </>
  )
}

export default StopTimingEditControl
